import {
    Stack,
    TextField,
    Button,
    Box,
    CircularProgress,
    Modal,
    Switch,
    Typography,
    LinearProgress,
    Accordion,
    AccordionSummary,
    AccordionDetails,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useSnackbar } from 'notistack';
import { AgeInput } from '~/shared/ui/inputs/AgeInput';
import { CallToAction } from '~/shared/ui/inputs/CallToAction';
import { CheckboxWithLabel } from '~/shared/ui/checkboxes/CheckboxWithLabel';
import { DevicesSelect } from '~/shared/ui/selectors/DevicesSelect';
import { GeoSelect2 } from '~/shared/ui/selectors/GeoSelect2';
import { MultipleTextInput } from '~/shared/ui/inputs/MultipleTextInput';
import { ScheduleModal } from '~/shared/ui/modals/ScheduleModal';
import { scheduleDefaults } from '~/shared/const/scheduleDefaults';
import { LanguageSelector2 } from '~/shared/ui/selectors/LanguageSelector2';
import { AuditorySelect2 } from '~/shared/ui/selectors/AuditorySelect2';
import { useCreateFileMutation } from '~/features/files/model/api/files-api';
import { validateTemplate } from '~/shared/utils/validateTemplate';
import { defaultFormState } from '~/shared/const/templateDefaults';
import { daysOfWeekEN } from '~/shared/const/app';
import { useCreateTemplateCampaignMutation } from '~/features/templates/model/api/templates-api';
import { ICreateTemplateModalProps } from '~/shared/types/types';
import { PlacementsGroup } from '../inputs/PlacementsGroup';
import { buildTemplateData } from '~/shared/utils/buildTemplateData';
import { uploadImage, cropImageToAspect, getPreviewUrl } from '~/shared/utils/uploadImage';
import { LogoDropzone } from '~/shared/ui/inputs/LogoDropzone'; 
import { buildFinalUrlSuffixPreview } from '~/shared/utils/buildFinalUrlSuffixPreview';
import { DEFAULT_CTA_LANGUAGE } from '~/shared/const/callToAction';
import { useGetCtaDictionaryOptionsQuery } from '~/features/dictionaries/model/api/cta-dictionary-api';
import { BudgetRangeSlider } from '~/shared/ui/inputs/BudgetRangeSlider';

export const CreateTemplateModal: React.FC<ICreateTemplateModalProps> = ({ open, handleClose }) => {

    // Состояния формы
    const [schedule, setSchedule] = useState(scheduleDefaults);
    const [lang, setLang] = useState([]);

    const [formState, setFormState] = useState({
        ...defaultFormState,
        placements: undefined
    });

    // Состояния UI 
    const [uiState, setUiState] = useState({
        openCreateTemplate: false,
        openImage: false,
        isLogoOpen: false,
        visible: false,
        headlineError: false,
        descriptionError: false,
        budgetError: false,
    });

    const [sourceImageUploading, setSourceImageUploading] = useState<{done: number; total: number} | null>(null);

    const [logoUploading, setLogoUploading] = useState<{done: number; total: number} | null>(null);

    const [createTemplate, { isLoading }] = useCreateTemplateCampaignMutation();
    const [uploadFile] = useCreateFileMutation();
    const { data: ctaDictionaryOptions } = useGetCtaDictionaryOptionsQuery();

    const { enqueueSnackbar } = useSnackbar();
    const ctaLanguages = useMemo(
        () => ctaDictionaryOptions?.data?.languages?.length
            ? ctaDictionaryOptions.data.languages
                .filter(item => item.is_active !== false)
                .map(item => ({ value: item.code, label: item.label }))
            : undefined,
        [ctaDictionaryOptions],
    );
    const ctaItems = useMemo(
        () => ctaDictionaryOptions?.data?.items?.length
            ? ctaDictionaryOptions.data.items.filter(item => item.is_active !== false)
            : undefined,
        [ctaDictionaryOptions],
    );
    const finalUrlSuffixPreview = useMemo(() => {
        return buildFinalUrlSuffixPreview({
            customerIdPlaceholder: '123-456-7890',
            appendTag: formState.append_tag,
            customUtm: formState.custom_utm,
            finalUrlSuffix: formState.final_url_suffix,
        });
    }, [formState.append_tag, formState.custom_utm, formState.final_url_suffix]);

    // Обработчики модальных окон
    const toggleModal = useCallback((key: keyof typeof uiState) => {
        setUiState(prev => ({ ...prev, [key]: !prev[key] }));
    }, []);

    // Общий обработчик изменений формы
    const handleFormChange = useCallback(
        (field: keyof typeof formState, value: any) => {
            if (typeof value === 'function') {
                setFormState(prev => ({ ...prev, [field]: value((prev as any)[field]) }));
            } else {
                setFormState(prev => ({ ...prev, [field]: value }));
            }
        },
        [],
    );

    const syncLegacyAssetOptimization = useCallback((adaptiveLayouts: boolean | null, generatedVideos: boolean | null) => {
        return typeof adaptiveLayouts === 'boolean' &&
            typeof generatedVideos === 'boolean' &&
            adaptiveLayouts === generatedVideos
            ? adaptiveLayouts
            : null;
    }, []);

    const handleAssetAutomationChange = useCallback(
        (
            field: 'asset_automation_adaptive_layouts' | 'asset_automation_generated_videos',
            value: boolean | null,
        ) => {
            setFormState((prev: any) => {
                const next = { ...prev, [field]: value };
                next.optimized_targeting = syncLegacyAssetOptimization(
                    next.asset_automation_adaptive_layouts,
                    next.asset_automation_generated_videos,
                );
                return next;
            });
        },
        [syncLegacyAssetOptimization],
    );

    // Очистка формы
    const resetState = useCallback(() => {
        setSchedule(scheduleDefaults);
        setLang([]);
        setFormState(defaultFormState);
    }, []);

    // Валидация формы
    const isValid = useMemo(() => validateTemplate(formState, uiState.visible), [formState, uiState.visible]);

    const handleBudgetRangeChange = useCallback((value: [number, number]) => {
        const nextBudgetFrom = Math.max(1, Math.round(value[0]));
        const nextBudgetTo = Math.max(nextBudgetFrom, Math.round(value[1]));
        const hasError =
            !Number.isFinite(nextBudgetFrom) ||
            !Number.isFinite(nextBudgetTo) ||
            nextBudgetFrom < 1 ||
            nextBudgetTo < nextBudgetFrom;

        setUiState(prev => ({ ...prev, budgetError: hasError }));
        handleFormChange('budget_usd_from', String(nextBudgetFrom));
        handleFormChange('budget_usd_to', String(nextBudgetTo));
    }, [formState.budget_usd_from, formState.budget_usd_to, handleFormChange]);

    // Обработчики полей формы
    const handleTextArea = useCallback(
        (field: 'headlines' | 'descriptions' | 'marketing_link', value: string) => {
            const rows = value.split('\n'); //.map(item => item.trim());
            const isBadString = rows
                .map(item => (item.length !== 0 ? 1 : 0))
                .join('');
            const isBadStrings = isBadString.match('01');
            const count = rows.length;

            if (count > 1 && Number(isBadString) === 0) {
                setUiState(prev => ({ ...prev, [`${field}Error`]: true }));
                handleFormChange(field, rows.join('\n').slice(0, 50));
                return;
            }

            if (count <= 50) {
                handleFormChange(field, rows.join('\n'));
            }

            setUiState(prev => ({
                ...prev,
                [`${field}Error`]: !!(count <= 50 && isBadStrings?.length),
            }));
        },
        [handleFormChange],
    );

    // Отправка формы
    const submit = useCallback(
        async (e: React.FormEvent) => {
            e.preventDefault();

            const data = buildTemplateData(formState, uiState, {
                schedule: schedule,
                daysOfWeekEN: daysOfWeekEN,
                lang: lang,
                callToActionItems: ctaItems,
                callToActionLanguages: ctaLanguages,
            });

            try {
                const payload = await createTemplate(data) as any;
                if (payload.error) {
                    const message = payload?.error?.data?.message || 'Ошибка';
                    const errors = payload?.error?.data?.errors;
                    const details = errors
                        ? Object.entries(errors)
                            .flatMap(([field, list]: any) =>
                                Array.isArray(list)
                                    ? list.map((msg: string) => `${field}: ${msg}`)
                                    : [`${field}: ${String(list)}`]
                            )
                            .join('\n')
                        : '';

                    enqueueSnackbar(details ? `${message}\n${details}` : message, {
                        variant: 'error',
                        autoHideDuration: 3000,
                        style: { maxWidth: '600px' },
                        anchorOrigin: { horizontal: 'right', vertical: 'top' },
                    });
                    return;
                }

                resetState();
                handleClose();
                enqueueSnackbar('Шаблон успешно добавлен!', {
                    variant: 'success',
                    autoHideDuration: 3000,
                    style: { maxWidth: '600px' },
                    anchorOrigin: { horizontal: 'right', vertical: 'top' },
                });
            } catch (error: any) {
                const message = error?.data?.message || 'Ошибка';
                const errors = error?.data?.errors;
                const details = errors
                    ? Object.entries(errors)
                        .flatMap(([field, list]: any) =>
                            Array.isArray(list)
                                ? list.map((msg: string) => `${field}: ${msg}`)
                                : [`${field}: ${String(list)}`]
                        )
                        .join('\n')
                    : '';

                enqueueSnackbar(details ? `${message}\n${details}` : message, {
                    variant: 'error',
                    autoHideDuration: 3000,
                    style: { maxWidth: '600px' },
                    anchorOrigin: { horizontal: 'right', vertical: 'top' },
                });
            }
        },
        [formState, uiState, schedule, lang, ctaItems, ctaLanguages, createTemplate, enqueueSnackbar, resetState, handleClose],
    );

    const handleUploadLogo = useCallback(
        async (file: File) => {
            await uploadImage({
                file,
                type: 'logo',
                uploadFile,
                handleFormChange,
                formState,
                field: 'logo',
                persistPath: true,
            });
        },
        [uploadFile, handleFormChange, formState],
    );

    const handleUploadLogos = useCallback(
        async (files: File[]) => {
            setLogoUploading({ done: 0, total: files.length });
            let done = 0;
            try {
                await files.reduce(async (chain, file) => {
                    await chain;
                    const cropped = await cropImageToAspect(file, 1).catch((error: any) => {
                        enqueueSnackbar(error?.message || `Не удалось обработать файл "${file.name}" перед загрузкой.`, {
                            variant: 'error',
                            autoHideDuration: 5000,
                            style: { maxWidth: '700px' },
                            anchorOrigin: { horizontal: 'right', vertical: 'top' },
                        });
                        return null;
                    });
                    if (cropped) {
                        await handleUploadLogo(cropped).catch(() => undefined);
                    }
                    done += 1;
                    setLogoUploading({ done, total: files.length });
                }, Promise.resolve());
            } finally {
                setLogoUploading(null);
            }
        },
        [handleUploadLogo],
    );

    const handleRemoveLogo = useCallback(
        (index: number) => {
            const paths = [...(formState.logo || [])];
            paths.splice(index, 1);
            handleFormChange('logo', paths);
        },
        [formState.logo, handleFormChange],
    );

    const handleUploadSourceImage = useCallback(
        async (file: File) => {
            try {
                setSourceImageUploading({ done: 0, total: 1 });
                await uploadImage({
                    file,
                    type: 'marketing_image',
                    uploadFile,
                    handleFormChange,
                    formState,
                    field: 'source_image',
                    persistPath: true,
                });
                setSourceImageUploading({ done: 1, total: 1 });
            } catch {
            } finally {
                setTimeout(() => setSourceImageUploading(null), 300);
            }
        },
        [formState, uploadFile, handleFormChange],
    );

    const handleRemoveSourceImage = useCallback((index: number) => {
        const paths = [...(formState.source_image || [])];
        paths.splice(index, 1);
        handleFormChange('source_image', paths);
    }, [formState.source_image, handleFormChange]);

    const handleMarketingFormatToggle = useCallback((format: string) => {
        handleFormChange('marketing_image_formats', (prev: string[] = []) =>
            prev.includes(format)
                ? prev.filter(item => item !== format)
                : [...prev, format]
        );
    }, [handleFormChange]);

    return (
        <Modal open={open} onClose={handleClose}>
            <Box sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: 900,
                bgcolor: 'background.paper',
                borderRadius: '12px',
                boxShadow: 24,
                pt: 3,
                px: 3,
                pb: 3,
            }}>
                <form onSubmit={submit}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
                        <Typography variant="h5" component="h5">
                            Создать шаблон
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            * - Поля, обязательные для заполнения
                        </Typography>
                    </Stack>
                    <Box
                        display="flex"
                        gap={3}
                        pb={0}
                        sx={{
                            maxHeight: '70vh',
                            overflowY: 'auto',
                            paddingLeft: '8px',
                            paddingRight: '8px',
                        }}>
                        {/* Левая колонка */}
                        <Box flex={1} display="flex" flexDirection="column" gap={1.5}>
                            <Typography fontWeight={600} variant="subtitle2">
                                Имя шаблона: *
                            </Typography>
                            <TextField
                                value={formState.name}
                                onChange={e => handleFormChange('name', e.target.value)}
                            />
                            <GeoSelect2
                                value={formState.geo}
                                onChange={e => handleFormChange('geo', e.target.value)}
                            />
                            <Box>
                                <Typography fontWeight={600} variant="subtitle2" mb={0.5}>
                                    Бюджет (USD): *
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                    Укажите диапазон в базовой валюте USD. Для каждого аккаунта итоговый бюджет будет выбран
                                    в этом диапазоне и затем автоматически конвертирован в валюту профиля.
                                </Typography>
                                <BudgetRangeSlider
                                    value={[
                                        Math.max(1, Number(formState.budget_usd_from) || 1),
                                        Math.max(1, Number(formState.budget_usd_to) || Number(formState.budget_usd_from) || 1),
                                    ]}
                                    onChange={handleBudgetRangeChange}
                                    error={uiState.budgetError}
                                />
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                                    Базовая валюта: USD
                                </Typography>
                            </Box>
                            <Box>
                                <Typography fontWeight={600} variant="subtitle2" mb={0.5}>
                                    Asset optimization
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                    Настройки повторяют блок Google Ads: image и video automation
                                    управляются отдельно, а при сбросе используется поведение Google по умолчанию.
                                </Typography>
                                <Stack spacing={1}>
                                    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 1.5 }}>
                                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                                            <Box>
                                                <Typography fontWeight={600}>Image</Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    Adaptive layouts
                                                </Typography>
                                            </Box>
                                            <Stack direction="row" spacing={0.5}>
                                                <Button
                                                    size="small"
                                                    variant={formState.asset_automation_adaptive_layouts === true ? 'contained' : 'outlined'}
                                                    onClick={() => handleAssetAutomationChange('asset_automation_adaptive_layouts', true)}
                                                >
                                                    On
                                                </Button>
                                                <Button
                                                    size="small"
                                                    color={formState.asset_automation_adaptive_layouts === false ? 'inherit' : 'primary'}
                                                    variant={formState.asset_automation_adaptive_layouts === false ? 'contained' : 'outlined'}
                                                    onClick={() => handleAssetAutomationChange('asset_automation_adaptive_layouts', false)}
                                                >
                                                    Off
                                                </Button>
                                            </Stack>
                                        </Stack>
                                        <Stack direction="row" justifyContent="space-between" alignItems="center" mt={1}>
                                            <Typography variant="caption" color="text.secondary">
                                                Сейчас: {formState.asset_automation_adaptive_layouts === null
                                                    ? 'По умолчанию Google'
                                                    : formState.asset_automation_adaptive_layouts ? 'On' : 'Off'}
                                            </Typography>
                                            <Button
                                                size="small"
                                                sx={{ px: 0, textTransform: 'none' }}
                                                onClick={() => handleAssetAutomationChange('asset_automation_adaptive_layouts', null)}
                                            >
                                                По умолчанию
                                            </Button>
                                        </Stack>
                                    </Box>

                                    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 1.5 }}>
                                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                                            <Box>
                                                <Typography fontWeight={600}>Video</Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    Generated videos
                                                </Typography>
                                            </Box>
                                            <Stack direction="row" spacing={0.5}>
                                                <Button
                                                    size="small"
                                                    variant={formState.asset_automation_generated_videos === true ? 'contained' : 'outlined'}
                                                    onClick={() => handleAssetAutomationChange('asset_automation_generated_videos', true)}
                                                >
                                                    On
                                                </Button>
                                                <Button
                                                    size="small"
                                                    color={formState.asset_automation_generated_videos === false ? 'inherit' : 'primary'}
                                                    variant={formState.asset_automation_generated_videos === false ? 'contained' : 'outlined'}
                                                    onClick={() => handleAssetAutomationChange('asset_automation_generated_videos', false)}
                                                >
                                                    Off
                                                </Button>
                                            </Stack>
                                        </Stack>
                                        <Stack direction="row" justifyContent="space-between" alignItems="center" mt={1}>
                                            <Typography variant="caption" color="text.secondary">
                                                Сейчас: {formState.asset_automation_generated_videos === null
                                                    ? 'По умолчанию Google'
                                                    : formState.asset_automation_generated_videos ? 'On' : 'Off'}
                                            </Typography>
                                            <Button
                                                size="small"
                                                sx={{ px: 0, textTransform: 'none' }}
                                                onClick={() => handleAssetAutomationChange('asset_automation_generated_videos', null)}
                                            >
                                                По умолчанию
                                            </Button>
                                        </Stack>
                                    </Box>
                                </Stack>
                            </Box>
                            <MultipleTextInput
                                title="Заголовки: *"
                                maxRows={50}
                                value={formState.headlines}
                                isError={uiState.headlineError}
                                onChange={e => handleTextArea('headlines', e.target.value)}
                            />
                            <MultipleTextInput
                                title="Описания: *"
                                maxRows={50}
                                value={formState.descriptions}
                                isError={uiState.descriptionError}
                                onChange={e => handleTextArea('descriptions', e.target.value)}
                            />

                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                <Typography variant="h6" component="h6" sx={{ fontSize: '14px' }}>
                                    Логотипы (1:1): *
                                </Typography>
                                <Typography variant="body2">{(formState.logo || []).length} файлов</Typography>
                            </Stack>
                            <LogoDropzone
                                value={formState.logo || []}
                                onFiles={handleUploadLogos}
                                onRemove={handleRemoveLogo}
                                uploading={logoUploading}
                                maxFilesPerBatch={30}
                            />

                            <Box display="flex" alignItems="center">
                                <Typography>Marketing изображение</Typography>
                                <Switch
                                    checked={uiState.visible}
                                    onChange={() => toggleModal('visible')}
                                    sx={{ mx: 1 }}
                                />
                                <Typography>Ссылка на YouTube</Typography>
                            </Box>

                            {!uiState.visible ? (
                                <>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                        Загрузите исходное изображение и выберите форматы, которые нужно автоматически подготовить.
                                        Для запуска Demand Gen нужен минимум один из форматов: 1:1 или 1.91:1.
                                    </Typography>
                                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                                        <Typography variant="h6" component="h6" sx={{ fontSize: '14px' }}>
                                            Marketing image:
                                        </Typography>
                                        <Typography variant="body2">{(formState.source_image || []).length} файлов</Typography>
                                    </Stack>
                                    <Button component="label" variant="outlined">
                                        + Добавить исходное изображение
                                        <input
                                            hidden
                                            type="file"
                                            accept="image/png, image/jpeg"
                                            onChange={async e => {
                                                const file = e.target.files?.[0];
                                                if (file) {
                                                    await handleUploadSourceImage(file);
                                                }
                                                e.currentTarget.value = '';
                                            }}
                                        />
                                    </Button>
                                    {sourceImageUploading && (
                                        <Box mt={1}>
                                            <Typography variant="caption">Загрузка: {sourceImageUploading.done}/{sourceImageUploading.total}</Typography>
                                            <LinearProgress variant="determinate" value={Math.min(100, Math.round((sourceImageUploading.done / sourceImageUploading.total) * 100))} sx={{ mt: 0.5 }} />
                                        </Box>
                                    )}
                                    {(formState.source_image || []).length > 0 && (
                                        <Box display="flex" gap={1} flexWrap="wrap" mt={0.5}>
                                            {(formState.source_image || []).map((item: File | string, index: number) => (
                                                <Box key={index} sx={{ position: 'relative', width: 72, height: 72 }}>
                                                    <Box
                                                        component="img"
                                                        src={getPreviewUrl(item)}
                                                        sx={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}
                                                    />
                                                    <Button
                                                        size="small"
                                                        color="error"
                                                        sx={{ minWidth: 0, position: 'absolute', top: -10, right: -10, bgcolor: 'background.paper', borderRadius: '50%' }}
                                                        onClick={() => handleRemoveSourceImage(index)}
                                                    >
                                                        ×
                                                    </Button>
                                                </Box>
                                            ))}
                                        </Box>
                                    )}

                                    <Box p={1.5} border="1px solid" borderColor="divider" borderRadius={2}>
                                        <Typography fontWeight={600} variant="subtitle2" mb={0.5}>
                                            Форматы адаптации
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                            Система автоматически подготовит версии под отмеченные aspect ratio из исходного изображения.
                                        </Typography>
                                        <Stack direction="row" flexWrap="wrap" gap={0.5}>
                                            <CheckboxWithLabel
                                                value={(formState.marketing_image_formats || []).includes('square')}
                                                title="1:1"
                                                onChange={() => handleMarketingFormatToggle('square')}
                                            />
                                            <CheckboxWithLabel
                                                value={(formState.marketing_image_formats || []).includes('landscape')}
                                                title="1.91:1"
                                                onChange={() => handleMarketingFormatToggle('landscape')}
                                            />
                                            <CheckboxWithLabel
                                                value={(formState.marketing_image_formats || []).includes('portrait')}
                                                title="4:5"
                                                onChange={() => handleMarketingFormatToggle('portrait')}
                                            />
                                            <CheckboxWithLabel
                                                value={(formState.marketing_image_formats || []).includes('story')}
                                                title="9:16"
                                                onChange={() => handleMarketingFormatToggle('story')}
                                            />
                                        </Stack>
                                    </Box>
                                </>
                            ) : (
                                <MultipleTextInput
                                    title="Ссылки на YouTube: *"
                                    maxRows={5}
                                    value={formState.marketing_link}
                                    onChange={e => handleTextArea('marketing_link', e.target.value)}
                                />
                            )}
                        </Box>

                        {/* Правая колонка */}
                        <Box flex={1} display="flex" flexDirection="column" gap={1.5}>
                            <PlacementsGroup
                                formState={formState}
                                setFormState={setFormState}
                            />
                            <CallToAction
                                value={formState.call_to_action_key}
                                language={formState.call_to_action_language || DEFAULT_CTA_LANGUAGE}
                                onChange={e =>
                                    handleFormChange('call_to_action_key', e.target.value)
                                }
                                onLanguageChange={e =>
                                    handleFormChange('call_to_action_language', e.target.value)
                                }
                            />
                            <AuditorySelect2
                                handleFormChange={handleFormChange}
                                formState={formState}
                            />
                            <ScheduleModal schedule={schedule} setSchedule={setSchedule} />
                            <DevicesSelect
                                friendly
                                devices={formState.devices}
                                handleFormChange={handleFormChange}
                            />
                            <LanguageSelector2 lang={lang} setLang={setLang as any} />
                            <AgeInput
                                ageFrom={formState.ageFrom ?? ''}
                                onChangeFrom={e =>
                                    handleFormChange('ageFrom', Number(e.target.value) || null)
                                }
                                ageTo={formState.ageTo ?? ''}
                                onChangeTo={e =>
                                    handleFormChange('ageTo', Number(e.target.value) || null)
                                }
                                unspecified_age={formState.unspecified_age}
                                onChangeUnspecifiedAge={(e: any) =>
                                    handleFormChange('unspecified_age', e.target.checked)
                                }
                            />
                            <Box mt={0} p={1.5} bgcolor="action.hover" borderRadius={1}>
                                <CheckboxWithLabel
                                    value={formState.append_tag}
                                    title="Автоматически вставлять тег конверсии"
                                    onChange={e => handleFormChange('append_tag', e.target.checked)}
                                />
                                <Typography fontWeight={600} variant="subtitle2" mb={0.5}>Тег конверсии</Typography>
                                <TextField
                                    fullWidth
                                    size="small"
                                    placeholder="AW-123456789/AbCdEfGhIjK"
                                    value={formState.custom_utm || ''}
                                    onChange={e => handleFormChange('custom_utm', e.target.value)}
                                    helperText={
                                        formState.append_tag
                                            ? 'Если поле пустое, тег будет подставлен автоматически. Если заполнено, будет использовано значение из поля.'
                                            : 'Автоподстановка выключена. Если поле заполнено, будет использовано значение из поля; если пустое, тег не добавится.'
                                    }
                                />
                            </Box>
                            <Box>
                                <Typography fontWeight={600} variant="subtitle2" mb={0.5}>Final URL suffix</Typography>
                                <TextField
                                    fullWidth
                                    size="small"
                                    placeholder="utm_source=google&utm_medium=cpc"
                                    value={formState.final_url_suffix || ''}
                                    onChange={e => handleFormChange('final_url_suffix', e.target.value)}
                                    helperText="Пользовательский suffix будет добавлен после системных параметров a и tag."
                                />
                                <Typography fontWeight={600} variant="subtitle2" mb={0.5} mt={1}>Предпросмотр итогового suffix</Typography>
                                <Box
                                    sx={{
                                        px: 1.5,
                                        py: 1.25,
                                        borderRadius: 1,
                                        bgcolor: 'action.hover',
                                        border: '1px solid',
                                        borderColor: 'divider',
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontFamily: 'monospace',
                                            fontSize: '0.95rem',
                                            wordBreak: 'break-all',
                                        }}
                                    >
                                        {finalUrlSuffixPreview}
                                    </Typography>
                                </Box>
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                    В шаблоне показан пример отображения с маской.
                                </Typography>
                            </Box>
                        </Box>
                    </Box>

                    {/* Кнопки действий */}
                    <Box display="flex" gap={1.5} pt={1.5}>
                        <Button
                            fullWidth
                            variant="outlined"
                            color="secondary"
                            onClick={handleClose}>
                            Отмена
                        </Button>
                        <Button
                            fullWidth
                            variant="contained"
                            type="submit"
                            disabled={!isValid || isLoading}
                        >
                            {isLoading && <CircularProgress size={24} sx={{ mr: 1 }} />}
                            Создать
                        </Button>
                    </Box>
                </form>
            </Box>
        </Modal>
    );
};
